import { ImageApi } from 'images/modules/ImagesModule/ImagesModule.api';
import { Image } from 'images/modules/ImagesModule/ImagesModule.types';
import { ImagesUtils } from 'images/modules/ImagesModule/ImagesModule.utils';
import { AbortedError } from 'infra/openstack/OpenStackRPC';
import { AppConfigurationUtils } from 'marketplace/utils/AppConfigurationUtils';
import * as R from 'ramda';
import { metrics } from 'system/libs/metrics';
import { YandexMetrica } from 'utilities/YandexMetrica';
import { arrayHelper } from 'utils/arrayHelper';
import { ModuleListReloader } from 'utils/ModuleListReloader/ModuleListReloader';
import { ExtendedXhrOptions } from 'utils/OpenStackApiHelper/OpenStackApiHelper';
import { reduxHelper } from 'utils/reduxHelper';
import { stateHelper } from 'utils/stateHelper';

import { CurrentUserSelectors } from 'reactApp/modules/CurrentUserModule/CurrentUserModule';

type State = {
    list: Image[];
    isLoaded: boolean;
    isLoading: boolean;
    hasError: boolean;
};

const imagesModule = stateHelper.createModuleState<State>('images', {
    list: [],
    isLoaded: false,
    isLoading: false,
    hasError: false,
});

//#region actions
const loadList = () => async (dispatch) => {
    dispatch(imagesModule.setPart('isLoading', true));

    try {
        const images = await _loadAllImages();

        dispatch(
            imagesModule.mergeModuleState({
                list: images,
                isLoaded: true,
                isLoading: false,
            }),
        );

        return images;
    } catch (error) {
        dispatch(
            imagesModule.mergeModuleState({
                isLoaded: false,
                isLoading: false,
                hasError: true,
            }),
        );

        // TODO: parse error and show notification
        throw error;
    }
};

const _loadAllImages = async () => {
    const limit = 100;
    let images = await ImageApi.loadList({ limit });

    let fetchCounter = 1;
    while (images.length === fetchCounter * limit) {
        const newImages = await ImageApi.loadList({
            limit,
            marker: images[images.length - 1].id,
        });
        images = [...images, ...newImages];
        fetchCounter++;
    }

    return images;
};

const _listReloader = new ModuleListReloader({
    loadList,
    selectIsLoaded: getIsLoaded,
});

const removeImage = reduxHelper.makeRemoveAction({
    module: imagesModule,
    apiMethod: ImageApi.removeImage,
    entityName: 'image',
    entityNamePublic: 'образ',
});
const removeImages = reduxHelper.multiplyAction(removeImage);

function getOnProgressWithProportionTime(
    onprogress: ({ loaded, total }: { loaded: number; total: number }) => void,
    {
        proportion,
        previousProgress,
    }: ReturnType<typeof getProportionOptions>[0],
) {
    return (progressEvent: ProgressEvent) => {
        const total = (progressEvent.total * 100) / proportion;
        const loaded = progressEvent.loaded + (previousProgress * total) / 100;
        onprogress({
            loaded,
            total,
        });
    };
}

function getProportionOptions(proportions: number[]) {
    return proportions.map((proportion, index) => {
        return {
            proportion,
            previousProgress: R.sum(proportions.slice(0, index)),
        };
    });
}

const _sendMetrics = () => {
    metrics.send({
        eventName: 'PlatformEvents',
        category: 'mcsplatform',
        action: 'image',
    });

    YandexMetrica.reachGoal('image');
};

type CreateImage = {
    file: File;
    name: string;
    xhrOptions: ExtendedXhrOptions;
    projectsIds?: string[];
};

const createImage = ({
    file,
    name,
    xhrOptions: { onprogress, saveAbortFunction },
    projectsIds = [],
}: CreateImage) => async () => {
    const proportionOptions = getProportionOptions([5, 90, 5]);
    let needRemoveImage = false;
    const abortFirstRequest = () => {
        needRemoveImage = true;
    };

    const { id } = await ImageApi.createImage(name, {
        onprogress:
            onprogress &&
            getOnProgressWithProportionTime(onprogress, proportionOptions[0]),
        saveAbortFunction: () =>
            saveAbortFunction && saveAbortFunction(abortFirstRequest),
    });
    ImageApi.shareWithProjects(id, projectsIds);
    try {
        if (needRemoveImage) {
            throw new AbortedError();
        }
        await ImageApi.uploadImageData(id, file, {
            onprogress:
                onprogress &&
                getOnProgressWithProportionTime(
                    onprogress,
                    proportionOptions[1],
                ),
            saveAbortFunction,
        });
        await ImageApi.importImage(id, {
            onprogress:
                onprogress &&
                getOnProgressWithProportionTime(
                    onprogress,
                    proportionOptions[2],
                ),
            saveAbortFunction,
        });

        _sendMetrics();
    } catch (error) {
        await ImageApi.removeImage(id);
        throw error;
    }
};

export const ImagesActions = {
    loadList,
    removeImages,
    addConsumer: _listReloader.addConsumer,
    removeConsumer: _listReloader.removeConsumer,
    createImage,
};
//#endregion

//#region selectors
const getList = (state) =>
    getNotRemovedImages(imagesModule.getModuleState(state).list);
export type ImageExtended = ReturnType<typeof getExtendedImage>;
function getExtendedList(state) {
    const list = getList(state);
    return list.map((image) => getExtendedImage(image));
}
function getExtendedImage(image: Image) {
    return {
        ...image,
        customFields: {
            isProcessing: ImagesUtils.getIsProcessing(image.status),
            activityColor: ImagesUtils.getColorByStatus(image.status),
        },
    };
}
function getNotPublicImages(state) {
    const images = getExtendedList(state);
    return images.filter(
        (image) =>
            image.visibility !== 'public' &&
            image.sid !== 'outdated' &&
            _isNotSnapshot(image),
    );
}

function getNotRemovedImages(images: Image[]) {
    return images.filter((image) => image.status !== 'deleted');
}

const _isImagePublic = (image: Image) => !image.sid || image.sid === 'ml';
const _filterPublicImages = (images: Image[]) => images.filter(_isImagePublic);
const _isNotSnapshot = (image: Image) =>
    !(
        typeof image.block_device_mapping === 'string' ||
        image.image_type === 'snapshot'
    );
const _filterImagesByTypes = (images: Image[]) => images.filter(_isNotSnapshot);
const _filterLearningImages = (images: Image[]) =>
    images.filter((image) => image.sid === 'ml');

const IMAGES_ORDER_MASKS = [
    /centos.*7.4.*/i,
    /centos.*6.9.*/i,
    /centos.*/i,
    /ubuntu.*16.*/i,
    /ubuntu.*14.*/i,
    /ubuntu.*/i,
    /windows.*2016.*ru.*/i,
    /windows.*2016.*en.*/i,
    /windows.*2012.*ru.*/i,
    /windows.*2012.*en.*/i,
    /windows.*/i,
    /debian.*/i,
    /freebsd.*/i,
    /opensuse.*/i,
    /.*/,
];

const _sortImagesByNameMasks = (images: Image[]) =>
    arrayHelper.sortWithOrderedPredicates<Image>(
        images,
        IMAGES_ORDER_MASKS.map((regex) => (image) => regex.test(image.name)),
    );

const _getPublicList = R.pipe(
    getList,
    _filterPublicImages,
    _filterImagesByTypes,
    _sortImagesByNameMasks,
);

const _getLearningList = R.pipe(getList, _filterLearningImages);

const getListForUserChoice = (state, props: { service?: string } = {}) =>
    props.service === 'learning'
        ? _getLearningList(state) || []
        : _getPublicList(state);

const getItem = (state, id) => getList(state).find(R.propEq('id', id));

const getImageByTemplate = (state, templateJson) =>
    AppConfigurationUtils.getImage({ images: getList(state), templateJson });

const getIsWindows = (state, id) => {
    const image = getItem(state, id);
    return ImagesUtils.getIsWindows(image);
};
const getIsBitrix = (state, id) => {
    const image = getItem(state, id);
    return ImagesUtils.getIsBitrix(image);
};

function getIsLoaded(state) {
    return imagesModule.getModuleState(state).isLoaded;
}

const getCurrentProjectImages = (state) => {
    const list = getList(state);
    const currentProjectId = CurrentUserSelectors.getPid(state);
    return list.filter((image) => {
        const project = CurrentUserSelectors.getProjectByProjectOSId(
            state,
            image.owner,
        );
        return project && project.pid === currentProjectId;
    });
};

export const ImagesSelectors = {
    getList,
    getListForUserChoice,
    getNotPublicImages,
    getCurrentProjectImages,

    getItem,
    getImageByTemplate,

    getIsLoading: (state) => imagesModule.getModuleState(state).isLoading,
    getIsLoaded,
    getHasError: (state) => imagesModule.getModuleState(state).hasError,

    getIsWindows,
    getIsBitrix,
};
//#endregion

export { ImagesUtils };
